import { onMounted, watch } from 'vue';
import { storeToRefs } from 'pinia';

import { addToast } from '@/helpers/vfm/toast-helper';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { hasUpgradeSessionCookie } from '@/features/jw-pro/helpers/upgradeSession';

const TOAST_DURATION = 15000;

/**
 * Tells subscribers their account was upgraded to JustWatch Plus, once per account:
 * - existing Pro subscribers: the first session after the upgrade,
 * - subscribers who bought through a Pro modal: the session after the one they paid in,
 * - subscribers who bought through the Plus modal: never, they are marked as having seen it when they pay.
 */
export function usePlusUpgradeToast() {
	const { t } = useI18n();
	const userStore = useUserStore();
	const { isPremium, isLoggedIn, hasSeenPlusUpgradeToast, isUpgradeSession } = storeToRefs(userStore);
	const { webLocale, isJwtvCountry } = storeToRefs(useLanguageStore());

	function getJwtvLink() {
		return { to: { name: 'app.jwtv.landing-page', path: `/${webLocale.value}/justwatch-tv` } };
	}

	function showUpgradeToast() {
		if (!import.meta.client) return;
		// Plus is not available outside the JustWatch TV countries, their subscribers stay on Pro
		if (!isJwtvCountry.value) return;
		if (!isLoggedIn.value || !isPremium.value || hasSeenPlusUpgradeToast.value) return;

		// still the session the user paid in, the session cookie is gone once the browser has been closed
		if (isUpgradeSession.value && hasUpgradeSessionCookie()) return;

		addToast({
			title: t('WEBAPP_PLUS_UPGRADE_TOAST_TITLE') as string,
			message: t('WEBAPP_PLUS_UPGRADE_TOAST_MESSAGE') as string,
			iconSrc: `/${ASSETS_DIR}/img/icons/pro-modal/jwtv-plus-provider.svg`,
			link: { text: t('WEBAPP_PLUS_UPGRADE_TOAST_LINK') as string, ...getJwtvLink() },
			duration: TOAST_DURATION,
			showCloseButton: false,
		});

		userStore.endUpgradeSession();
	}

	// signing in (or the account turning premium) does not reload the app, so the toast also reacts to it
	onMounted(() => {
		watch([isLoggedIn, isPremium], showUpgradeToast);
	});

	return { showUpgradeToast };
}
